import { NgModule } from '@angular/core';
import { Routes, RouterModule } from '@angular/router';
import {LoginPageComponent} from "./pages/login-page/login-page.component";
import {EquipmentPageComponent} from "./pages/equipment-page/equipment-page.component";
import {ScottyLayoutComponent} from "./layout/scotty-layout/scotty-layout.component";
import {NotFoundComponent} from "./pages/not-found/not-found.component";
import {DashboardComponent} from "./pages/dashboard/dashboard.component";
import {AuthGuard} from "./guards/auth.guard";
import {CreateEquipmentPageComponent} from "./pages/equipment-page/create-equipment-page/create-equipment-page.component";
import {MaintenancePageComponent} from "./pages/maintenance-page/maintenance-page.component";
import {MaintenanceSingleChatPageComponent} from "./pages/maintenance-page/maintenance-single-chat-page/maintenance-single-chat-page.component";
import {PhysicalAssetPageComponent} from "./pages/equipment-page/physical-asset-page/physical-asset-page.component";
import {PersonnelPageComponent} from "./pages/personnel-page/personnel-page.component";
import {CreatePersonnelPageComponent} from "./pages/personnel-page/create-personnel-page/create-personnel-page.component";
import {FieldServicePageComponent} from "./pages/field-service-page/field-service-page.component";
import {CreateFieldServicePageComponent} from "./pages/field-service-page/create-field-service-page/create-field-service-page.component";
import {CalendarPageComponent} from "./pages/calendar-page/calendar-page.component";
import {TrainingPageComponent} from "./pages/training-page/training-page.component";
import {PublicTrainingGuard} from "./guards/public-training.guard";
import {TrainingPageAccessCodeComponent} from "./pages/training-page/training-page-access-code/training-page-access-code.component";
import {AuthenticatedVideocallPageComponent} from "./pages/authenticated-videocall-page/authenticated-videocall-page.component";
import {AuthenticatedVideocallClosedPageComponent} from "./pages/authenticated-videocall-page/authenticated-videocall-closed-page/authenticated-videocall-closed-page.component";
import {PrivacyPageComponent} from "./pages/privacy-page/privacy-page.component";
import {SkillPageComponent} from "./pages/skill-page/skill-page.component";
import {CalendarEventDetailPageComponent} from "./pages/calendar-page/calendar-event-detail-page/calendar-event-detail-page.component";
import {SettingsPageComponent} from "./pages/settings-page/settings-page.component";
import {FieldServiceType} from "./models/field-service";
import {ProcedurePageComponent} from "./pages/procedure-page/procedure-page.component";
import {CartographicConsolePageComponent} from "./pages/cartographic-console-page/cartographic-console-page.component";


const routes: Routes = [

  /* Public training */
  { path: 'training/:training-room', component: TrainingPageComponent, canActivate: [PublicTrainingGuard], data: { isAnonymousUser: true } },
  { path: 'training/:training-room/access-code', component: TrainingPageAccessCodeComponent },

  /* Authenticated videocall */
  { path: 'videocall', component: AuthenticatedVideocallPageComponent },
  { path: 'videocall/closed', component: AuthenticatedVideocallClosedPageComponent },

  // Authenticated pages
  {
    path: '',
    component: ScottyLayoutComponent,
    canActivate: [AuthGuard],
    children: [

      { path: '', pathMatch: 'full', redirectTo: 'equipment' }, // Change redirection to 'dashboard'

      /* Dashboard */
      // { path: 'dashboard', component: DashboardComponent },

      /* Equipment */
      {
        path: 'equipment',
        children: [

          /* Equipment list */
          { path: '', component: EquipmentPageComponent, },

          /* New equipment */
          { path: 'create', component: CreateEquipmentPageComponent },

          /* Equipment view */
          {
            path: ':id',
            children: [

              /* Edit equipment */
              { path: '', component: CreateEquipmentPageComponent, },

              /* Edit asset */
              {
                path: 'asset',
                children: [

                  { path: '', component: PhysicalAssetPageComponent },

                  { path: ':assetId', component: PhysicalAssetPageComponent },

                ]
              },

              /* Authoring */
              {
                path: 'authoring',
                children: [
                  { path: ':procedureId', component: ProcedurePageComponent },
                ],
              },

            ],
          },

        ],
      },

      /* Field services */
      {
        path: 'field-service',
        children: [

          /* Plant list */
          { path: '', component: FieldServicePageComponent, },

          /* New Plant */
          { path: 'create', component: CreateFieldServicePageComponent, data: { fieldServiceType: FieldServiceType.PLANT }, },

          /* Plant view */
          {
            path: ':plantId',
            children: [

              /* Plant view */
              { path: '', component: CreateFieldServicePageComponent, data: { fieldServiceType: FieldServiceType.PLANT }, },

              /* New Department */
              { path: 'create', component: CreateFieldServicePageComponent, data: { fieldServiceType: FieldServiceType.DEPARTMENT }, },

              /* Department view */
              {
                path: ':departmentId',
                children: [

                  /* Department view */
                  { path: '', component: CreateFieldServicePageComponent, data: { fieldServiceType: FieldServiceType.DEPARTMENT }, },

                  /* New Production Line */
                  { path: 'create', component: CreateFieldServicePageComponent, data: { fieldServiceType: FieldServiceType.PRODUCTION_LINE }, },

                  /* Production Line view */
                  {
                    path: ':productionLineId',
                    children: [

                      /* Production Line view */
                      { path: '', component: CreateFieldServicePageComponent, data: { fieldServiceType: FieldServiceType.PRODUCTION_LINE }, },

                      /* New Work Center */
                      { path: 'create', component: CreateFieldServicePageComponent, data: { fieldServiceType: FieldServiceType.WORK_CENTER }, },

                      /* New Work view */
                      { path: ':workCenterId', component: CreateFieldServicePageComponent, data: { fieldServiceType: FieldServiceType.WORK_CENTER }, },

                    ],
                  },

                ],
              },

            ],
          },

        ],
      },

      /* Personnel */
      {
        path: 'personnel',
        children: [

          /* Personnel list */
          { path: '', component: PersonnelPageComponent, },

          /* New personnel */
          { path: 'create', component: CreatePersonnelPageComponent },

          /* Personnel view */
          { path: ':id', component: CreatePersonnelPageComponent },

        ],
      },

      /* Skill */
      {
        path: 'skill',
        component: SkillPageComponent
      },

      /* Maintenance */
      {
        path: 'maintenance',
        children: [

          /* Live assistance and assistance history */
          { path: '', component: MaintenancePageComponent },

          /* Single chat maintenance */
          { path: ':maintenance-id', component: MaintenanceSingleChatPageComponent },

        ],
      },

      /* Calendar */
      {
        path: 'calendar',
        children: [

          /* Calendar page */
          { path: '', component: CalendarPageComponent },

          /* New Calendar event page */
          { path: 'event/create', component: CalendarEventDetailPageComponent, data: { isNew: true } },

          /* Calendar event detail page */
          { path: 'event/:id', component: CalendarEventDetailPageComponent, data: { isNew: false } },

        ]
      },

      /* Cartographic console */
      {
        path: 'cartographic-console',
        component: CartographicConsolePageComponent
      },

      /* Settings */
      {
        path: 'settings',
        component: SettingsPageComponent
      },

      /* Training */
      { path: 'internal-training/:training-room', component: TrainingPageComponent },

    ]
  },

  // Non authenticated pages
  {
    path: 'login',
    children: [
      { path: '', component: LoginPageComponent },
      { path: 'sso', component: LoginPageComponent, data: {sso: true} },
    ]
  },
  { path: 'privacy', component: PrivacyPageComponent },
  { path: '404', component: NotFoundComponent },
  { path: '**', redirectTo: '404' }

];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule]
})
export class AppRoutingModule { }
